import Spinner from '../ui/spinner';
import { User, Search, Calendar, MoreVertical } from 'lucide-react';

import { useTranslation } from '../../i18n';
import useListParams from '../../hooks/useListParams';
import { getImageUrl } from '../../utils/imageUrl';
import { formatDate } from '../../utils/formatDate';
import { IUserWithStats } from '../../types';
import Pagination from '../Pagination';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const AVATAR_COLOR = '#3b82f6';

type EFilter = 'all' | 'active' | 'banned';

interface IUserListViewProps {
  users: IUserWithStats[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  onViewDetail: (user: IUserWithStats) => void;
  onEdit: (user: IUserWithStats) => void;
  onBan: (user: IUserWithStats) => void;
  onMute: (user: IUserWithStats) => void;
  onDelete: (user: IUserWithStats) => void;
  onNotify: (user: IUserWithStats) => void;
}

const UserListView = ({
  users,
  total,
  totalPages,
  isLoading,
  onViewDetail,
  onBan,
  onMute,
  onDelete,
  onNotify,
}: IUserListViewProps) => {
  const { t } = useTranslation();
  const { search, filter, page, setSearch, setFilter, setPage } = useListParams(
    { filter: 'all' },
  );

  const filters: { key: EFilter; label: string }[] = [
    { key: 'all', label: t('common.all') },
    { key: 'active', label: t('common.active') },
    { key: 'banned', label: t('users.banned') },
  ];

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      {/* Search + Filter */}
      <Card className="p-4 gap-0">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Search className="w-5 h-5" />
            </span>
            <Input
              placeholder={t('users.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="UserManagement_List_searchInput"
            />
          </div>
          <div className="flex gap-2">
            {filters.map(({ key, label }) => (
              <Button
                key={key}
                variant={filter === key ? 'default' : 'outline'}
                onClick={() => setFilter(key)}
                data-testid={`UserManagement_List_filterButton_${key}`}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="gap-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('users.user')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('users.join_date')}</TableHead>
              <TableHead>{t('users.groups')}</TableHead>
              <TableHead>{t('users.rooms_joined')}</TableHead>
              <TableHead>{t('users.message_count')}</TableHead>
              <TableHead className="w-10 text-center">
                {t('common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => {
              const { id, username, displayName, isActive, createdAt, avatar } =
                user;

              return (
                <TableRow
                  key={id}
                  onClick={() => onViewDetail(user)}
                  className="cursor-pointer"
                  data-testid={`UserManagement_List_userRow_${id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0 overflow-hidden"
                        style={{
                          background: avatar ? 'transparent' : AVATAR_COLOR,
                        }}
                      >
                        {avatar ? (
                          <img
                            src={getImageUrl(avatar)!}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          (displayName || username).charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground">
                          {displayName}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <User className="w-3 h-3" /> {username}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={isActive ? 'success' : 'danger'}>
                      {isActive ? t('common.active') : t('users.banned')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> {formatDate(createdAt)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {user.spaces && user.spaces.length > 0 ? (
                        <>
                          {user.spaces.slice(0, 3).map((space) => (
                            <Badge
                              key={space.id}
                              variant="outline"
                              className="text-xs"
                            >
                              {space.name}
                            </Badge>
                          ))}
                          {user.spaces.length > 3 && (
                            <Badge
                              variant="outline"
                              className="text-xs text-muted-foreground"
                            >
                              +{user.spaces.length - 3}
                            </Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {user.roomCount ?? 0}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {user.messageCount?.toLocaleString() ?? 0}
                  </TableCell>
                  <TableCell
                    className="text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-testid={`UserManagement_List_rowActionsButton_${id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onViewDetail(user)}
                          data-testid={`UserManagement_List_viewDetailButton_${id}`}
                        >
                          {t('users.view_detail')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onNotify(user)}
                          data-testid={`UserManagement_List_sendNotificationButton_${id}`}
                        >
                          {t('users.send_notification')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onViewDetail(user)}
                          data-testid={`UserManagement_List_roomsParticipatedButton_${id}`}
                        >
                          {t('users.rooms_participated')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onBan(user)}
                          data-testid={`UserManagement_List_banUserButton_${id}`}
                        >
                          {user.isActive
                            ? t('users.ban_user')
                            : t('users.unban_user')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onMute(user)}
                          data-testid={`UserManagement_List_muteUserButton_${id}`}
                        >
                          {user.isMuted
                            ? t('users.unmute_user')
                            : t('users.mute_user')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => onDelete(user)}
                          data-testid={`UserManagement_List_deleteAccountButton_${id}`}
                        >
                          {t('users.delete_account')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={20}
          onPageChange={setPage}
          testId="UserManagement_List"
        />
      </Card>
    </>
  );
};

export default UserListView;
